import type { CompanyProfileDTO, Option, ProductPrice, VatTreatment } from 'ecosystem'
import { DEFAULT_COUNTRY, EU_COUNTRIES, VAT_RATES } from '../constants'
import { normalizeCountryCode } from './payment'
import { isNullable } from './general'
import { roundNumber } from './numbers'
import { getProductOriginalPrice, getProductPrice } from './products'

/**
 * VAT treatments that carry no VAT: the buyer self-accounts (reverse charge) or
 * the supply leaves the tax area entirely (export).
 */
const ZERO_RATED: readonly VatTreatment[] = [
  'EU_REVERSE_CHARGE',
  'EXPORT',
  'DOMESTIC_REVERSE_CHARGE'
]

export const isZeroRatedTreatment = (treatment: VatTreatment): boolean =>
  ZERO_RATED.includes(treatment)

const isEuCountry = (countryCode: string): boolean =>
  (EU_COUNTRIES as readonly string[]).includes(normalizeCountryCode(countryCode))

/**
 * A VAT number is usable for intra-EU reverse charge only if it starts with an
 * issuing country's ISO-2 code.
 *
 * @remarks
 * Deliberately a *format* check, not validation. The prefix may legitimately
 * differ from the company's address country — a company can be VAT-registered
 * in another member state — so only the accounting system can truly judge it.
 * We check the shape because the downstream cost of getting it wrong is severe:
 * Fortnox rejects a malformed number on a reverse-charge customer and skips the
 * **entire order**, writing nothing and never retrying, so the order silently
 * never reaches accounting.
 */
export const hasUsableVatNumber = (vatNumber?: Option<string>): boolean => {
  if (isNullable(vatNumber)) {
    return false
  }
  const trimmed = vatNumber.trim().toUpperCase()
  return /^[A-Z]{2}/.test(trimmed) && isEuCountry(trimmed.slice(0, 2))
}

/**
 * The buying company's country, resolved the same way the accounting push does
 * so the storefront and the ledger agree on the treatment: default BILLING
 * address, then any BILLING, then LEGAL, then the profile's own `country`.
 */
export const resolveBuyerCountry = (
  companyProfile?: Option<CompanyProfileDTO>
): string | undefined => {
  const addresses = companyProfile?.addresses ?? []
  const billing = addresses.filter((a) => a.type === 'BILLING' && a.country)

  const country =
    billing.find((a) => a.default)?.country ??
    billing[0]?.country ??
    addresses.find((a) => a.type === 'LEGAL' && a.country)?.country ??
    companyProfile?.country

  return isNullable(country) || !country ? undefined : normalizeCountryCode(country)
}

/**
 * Classify a buying company for VAT.
 *
 * An explicit `salesTerms.vatTreatment` always wins. When it is null — the
 * common case — derive from the company's country + VAT number relative to the
 * tenant's own jurisdiction:
 *
 * - buyer country == tenant country → `DOMESTIC`
 * - EU country with a usable VAT number → `EU_REVERSE_CHARGE` (0%)
 * - EU country without one → `EU_VAT` (destination rate, OSS)
 * - anything else → `EXPORT` (0%)
 *
 * A null treatment means "derive", never "assume domestic" — falling back to
 * the domestic rate is precisely how an international buyer ends up charged
 * VAT they do not owe.
 *
 * @param companyProfile - the buying company; absent (a consumer) → `DOMESTIC`
 * @param tenantCountry - the selling tenant's country, ISO-2
 */
export const resolveVatTreatment = (
  companyProfile?: Option<CompanyProfileDTO>,
  tenantCountry: string = DEFAULT_COUNTRY
): VatTreatment => {
  const explicit = companyProfile?.salesTerms?.vatTreatment
  if (!isNullable(explicit)) {
    return explicit
  }

  // No company profile at all — a consumer. Domestic rules, catalogue rate.
  if (!companyProfile) {
    return 'DOMESTIC'
  }

  const buyerCountry = resolveBuyerCountry(companyProfile)

  // Unknown country: stay domestic rather than guessing an export. Charging
  // VAT we could have avoided is recoverable; failing to charge VAT we owe is
  // not.
  if (!buyerCountry) {
    return 'DOMESTIC'
  }

  if (buyerCountry === normalizeCountryCode(tenantCountry)) {
    return 'DOMESTIC'
  }

  if (isEuCountry(buyerCountry)) {
    return hasUsableVatNumber(companyProfile.vatNumber) ? 'EU_REVERSE_CHARGE' : 'EU_VAT'
  }

  return 'EXPORT'
}

/**
 * A problem with the company's VAT number that the shopper should be told
 * about, because it silently changes what they pay — or silently breaks the
 * order downstream.
 *
 * - `MISSING` / `INVALID_FORMAT`: an EU company that would have been zero-rated
 *   under reverse charge is instead charged destination VAT. Reported only when
 *   the treatment was *derived*; an explicitly-set `EU_VAT` is a deliberate
 *   decision and is left alone.
 * - `REVERSE_CHARGE_UNVERIFIABLE`: the treatment says reverse charge but the
 *   number won't survive the accounting push — Fortnox rejects a malformed VAT
 *   number on such a customer and drops the **entire order**, writing nothing
 *   and never retrying. Only reachable through an explicit override, since the
 *   derivation never picks reverse charge without a usable number.
 */
export type VatNumberIssue = 'MISSING' | 'INVALID_FORMAT' | 'REVERSE_CHARGE_UNVERIFIABLE'

const resolveVatNumberIssue = (
  companyProfile: Option<CompanyProfileDTO> | undefined,
  treatment: VatTreatment
): VatNumberIssue | null => {
  if (!companyProfile) {
    return null
  }

  const hasNumber = !isNullable(companyProfile.vatNumber) && !!companyProfile.vatNumber.trim()
  const isReverseCharge =
    treatment === 'EU_REVERSE_CHARGE' || treatment === 'DOMESTIC_REVERSE_CHARGE'

  if (isReverseCharge && !hasUsableVatNumber(companyProfile.vatNumber)) {
    return 'REVERSE_CHARGE_UNVERIFIABLE'
  }

  // Only nag about the fallback when we chose it ourselves.
  const wasDerived = isNullable(companyProfile.salesTerms?.vatTreatment)
  if (treatment === 'EU_VAT' && wasDerived) {
    return hasNumber ? 'INVALID_FORMAT' : 'MISSING'
  }

  return null
}

/**
 * The VAT rate to charge a buyer on a line, given its treatment.
 *
 * `DOMESTIC` keeps whatever rate the line already carries (the catalogue rate —
 * products can be on reduced rates, so there is no single "domestic rate" to
 * substitute). `EU_VAT` takes the destination country's rate. Everything else
 * is zero-rated.
 *
 * @param domesticRate - the line's own rate, used for `DOMESTIC`
 */
export const resolveVatRate = (
  treatment: VatTreatment,
  buyerCountry: Option<string> | undefined,
  domesticRate: number
): number => {
  if (isZeroRatedTreatment(treatment)) {
    return 0
  }

  if (treatment === 'EU_VAT' && !isNullable(buyerCountry) && buyerCountry) {
    const normalized = normalizeCountryCode(buyerCountry)
    // Only trust the table for countries it actually knows — its lookup helper
    // falls back to SE, which would silently re-domesticate the sale.
    const destinationRate = VAT_RATES[normalized as keyof typeof VAT_RATES]
    return destinationRate ?? domesticRate
  }

  return domesticRate
}

/**
 * Move a VAT-inclusive price from one rate to another by re-basing it on its
 * net value.
 *
 * @remarks
 * Prices are stored GROSS everywhere in this codebase, and every payment
 * factory derives net from gross using the line's rate. So a rate can never be
 * changed on its own: zeroing the rate while leaving the price gross makes
 * net == gross and bills the VAT-inclusive figure as if it were net — an
 * overcharge that nothing downstream can detect, since each half looks valid
 * alone.
 *
 * When the two rates match this returns `gross` **untouched**, so every
 * domestic order keeps its exact existing number — no rounding drift is
 * introduced on paths that aren't actually changing.
 *
 * @param gross - the VAT-inclusive price at `fromRate`
 * @param net - the authoritative net price when the caller has one (the BE
 *   populates `priceWithoutVat` on every product read); preferred over
 *   recomputing so we agree with the server's rounding
 */
export const rebaseGross = (
  gross: number,
  net: Option<number>,
  fromRate: number,
  toRate: number
): number => {
  if (fromRate === toRate) {
    return gross
  }

  const netPrice = isNullable(net) ? gross / (1 + fromRate / 100) : net

  return roundNumber(netPrice * (1 + toRate / 100))
}

/**
 * The rate/price conversion a resolved buyer implies. Supplied by the
 * `useBuyerVat` hook; taken as a parameter so non-React code (order-line
 * builders, API handlers) can apply the same rule.
 */
export interface BuyerVatRates {
  /** How this buyer is classified for VAT. `DOMESTIC` for consumers. */
  treatment: VatTreatment
  /** The buying company's country (ISO-2), when there is one. */
  buyerCountry?: string
  /** True when no VAT may be charged — reverse charge or export. */
  isZeroRated: boolean
  /**
   * A VAT-number problem worth surfacing to the shopper, or `null` when there
   * is nothing to say. See {@link VatNumberIssue} — each case either costs the
   * buyer money or loses the order downstream, so none of them should stay
   * silent.
   */
  vatNumberIssue: VatNumberIssue | null
  /**
   * The single rate that applies to every line, or `null` when each line keeps
   * its own catalogue rate (`DOMESTIC`).
   *
   * Non-null only for treatments that override the catalogue uniformly: 0 for
   * reverse charge / export, the destination rate for `EU_VAT`. Lets callers
   * that work with cart *totals* rather than lines (the checkout summary)
   * convert exactly instead of re-walking every product.
   */
  flatRate: number | null
  /** The rate to charge for a line currently priced at `domesticRate`. Returns
   *  `domesticRate` unchanged for domestic buyers, so callers may apply it
   *  unconditionally. */
  resolveRate: (domesticRate: number) => number
  /** Move a VAT-inclusive price onto this buyer's rate. Pass the BE's net price
   *  when available so we match the server's rounding. A no-op when the rates
   *  already agree. */
  rebase: (gross: number, net: Option<number>, domesticRate: number) => number
}

/**
 * Build the rate/price conversion for a buying company.
 *
 * @remarks
 * Plain function, not a hook, because both sides of the checkout need it: the
 * React components building the cart, and the Next API handlers that construct
 * payment sessions server-side (where the company profile arrives from a
 * customer lookup rather than from context).
 *
 * @param tenantCountry - the selling tenant's country, ISO-2
 */
export const buyerVatRatesFor = (
  companyProfile?: Option<CompanyProfileDTO>,
  tenantCountry: string = DEFAULT_COUNTRY
): BuyerVatRates => {
  const treatment = resolveVatTreatment(companyProfile, tenantCountry)
  const buyerCountry = resolveBuyerCountry(companyProfile)

  const resolveRate = (domesticRate: number) =>
    resolveVatRate(treatment, buyerCountry, domesticRate)

  // `DOMESTIC` is the only treatment that leaves each line on its own rate —
  // products can sit on reduced rates, so there is nothing uniform to apply.
  // Zero-rated treatments flatten everything to 0; `EU_VAT` flattens to the
  // destination rate, unless the table doesn't know that country, in which case
  // the per-line catalogue rates stand.
  const flatRate = (() => {
    if (isZeroRatedTreatment(treatment)) return 0
    if (treatment === 'EU_VAT' && buyerCountry) {
      return VAT_RATES[buyerCountry as keyof typeof VAT_RATES] ?? null
    }
    return null
  })()

  return {
    treatment,
    buyerCountry,
    isZeroRated: isZeroRatedTreatment(treatment),
    vatNumberIssue: resolveVatNumberIssue(companyProfile, treatment),
    flatRate,
    resolveRate,
    rebase: (gross: number, net: Option<number>, domesticRate: number) =>
      rebaseGross(gross, net, domesticRate, resolveRate(domesticRate))
  }
}

/**
 * Re-base a carrier's shipping quote onto the buyer's VAT treatment.
 *
 * @remarks
 * Carrier widgets (Ingrid, PostNord, Nshift) and the store's own flat-fee
 * config all quote a price that is GROSS at the *domestic* rate — the carrier
 * has no idea who the buyer is. A zero-rated buyer owes no VAT on freight
 * either, so the quote has to come down to its net value rather than simply
 * being relabelled 0 %.
 *
 * Returns `{ cost, vatRate }` — both, because they can never move apart.
 * A no-op for domestic buyers.
 *
 * @param grossQuote - the carrier's price, VAT-inclusive at `domesticRate`
 */
export const shippingForBuyer = (
  grossQuote: number,
  domesticRate: number,
  buyerVat?: Option<BuyerVatRates>,
  /** The quote's exact net value, when the caller knows it — a merchant who
   *  configured the fee ex-VAT typed this number, so passing it keeps a
   *  zero-rated buyer on the figure that was written rather than on one
   *  round-tripped through the domestic rate. */
  netQuote?: Option<number>
): { cost: number; vatRate: number } => {
  if (!buyerVat) {
    return { cost: grossQuote, vatRate: domesticRate }
  }

  return {
    cost: buyerVat.rebase(grossQuote, netQuote ?? null, domesticRate),
    vatRate: buyerVat.resolveRate(domesticRate)
  }
}

/**
 * A re-baser for amounts the *store* prices rather than the catalogue — service
 * add-ons, flat fees, carrier quotes.
 *
 * @remarks
 * These carry no `vatRate` of their own: they are configured as gross figures
 * at the store's own rate, so they have to be moved onto the buyer's treatment
 * the same way product lines are. Zeroing the rate on a service row while
 * leaving its price gross bills the VAT-inclusive figure as if it were net —
 * the same silent overcharge, just on a line nobody thinks to check.
 *
 * Returns the amount unchanged when there is no buyer treatment to apply.
 */
export const storeGrossRebaser =
  (domesticRate: number, buyerVat?: Option<BuyerVatRates>) =>
  (gross: number): number =>
    buyerVat ? buyerVat.rebase(gross, null, domesticRate) : gross

/**
 * Price one product for a buyer: their rate, and the gross prices re-based onto
 * it.
 *
 * @remarks
 * The single place rate and price are changed together. Every order line —
 * top-level and sub-item, every payment provider, every flow — goes through
 * here, so the two halves can never drift apart.
 *
 * With no `buyerVat` (an anonymous shopper, or a caller that hasn't resolved
 * one) this returns today's values untouched.
 *
 * @param fallbackVatRate - used when the product carries no `vatRate` of its own
 */
export const priceProductForBuyer = (
  product: ProductPrice & { vatRate?: Option<number> },
  fallbackVatRate: number,
  buyerVat?: Option<BuyerVatRates>
): { vatRate: number; unitPrice: number; originalPrice: number } => {
  const domesticRate = product.vatRate ?? fallbackVatRate
  const grossUnit = getProductPrice(product)
  const grossOriginal = getProductOriginalPrice(product)

  if (!buyerVat) {
    return { vatRate: domesticRate, unitPrice: grossUnit, originalPrice: grossOriginal }
  }

  return {
    vatRate: buyerVat.resolveRate(domesticRate),
    unitPrice: buyerVat.rebase(grossUnit, getProductPrice(product, true), domesticRate),
    originalPrice: buyerVat.rebase(
      grossOriginal,
      getProductOriginalPrice(product, true),
      domesticRate
    )
  }
}
